import { OverlappingSourcingPurchaseResponse } from 'api-clients/monolith';
import { FormattedMessage } from 'react-intl';

import { CalendarIcon } from '@/components/icons/calendar';
import { Button } from '@/components/ui/button';
import { Typography } from '@/components/ui/typography';
import { formatShortDate } from '@/utils/date-util';

interface ConflictsProps {
  allOpeningsHaveConflicts: boolean;
  openingsWithConflicts?: string[];
  removeConflictingOpening: (openingId: string) => void;
  conflicts?: OverlappingSourcingPurchaseResponse['conflicts'];
}

export function Conflicts({
  allOpeningsHaveConflicts,
  openingsWithConflicts,
  removeConflictingOpening,
  conflicts,
}: ConflictsProps) {
  if (
    !openingsWithConflicts ||
    !conflicts ||
    openingsWithConflicts.length === 0
  )
    return null;

  return (
    <div className="flex gap-2 rounded bg-rose-50 p-2">
      <CalendarIcon className="text-fx-rose" />
      <div className="flex flex-col">
        <Typography variant="body1">
          {allOpeningsHaveConflicts ? (
            <FormattedMessage
              defaultMessage="All openings have campaigns that will overlap with this one. Please select different openings or remove this channel to proceed."
              description="Message for all openings having conflicts"
            />
          ) : (
            <FormattedMessage
              defaultMessage="{count} openings have campaigns that will overlap with this one. Please adjust the end date or remove these openings."
              description="Message for overlapping campaigns"
              values={{ count: openingsWithConflicts.length }}
            />
          )}
        </Typography>

        <ul className="list-disc pl-4">
          {conflicts.map((conflict, idx) => (
            // eslint-disable-next-line react/no-array-index-key
            <li key={`${conflict.opening_id}-${idx}`}>
              <Typography variant="body1">
                <span>{conflict.opening_name}</span>{' '}
                <span className="!text-fx-neutral-600 italic">
                  ({formatShortDate(conflict.start_date)} -{' '}
                  {formatShortDate(conflict.end_date)})
                </span>{' '}
                {!allOpeningsHaveConflicts && (
                  <Button
                    variant="link"
                    type="button"
                    onClick={() =>
                      removeConflictingOpening(conflict.opening_id)
                    }
                    className="!text-fx-rose-800 h-5"
                  >
                    <FormattedMessage
                      defaultMessage="Remove"
                      description="Button to remove the opening from the campaign"
                    />
                  </Button>
                )}
              </Typography>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
